import { ReactNode } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { IntlShape } from 'react-intl';
import {
  IconAbc,
  IconBraces,
  IconBrackets,
  IconCalendar,
  IconCheckbox,
  IconNumber123,
  IconQuestionMark,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import { Typography } from '@rossum/ui/material';
import { FormSchema } from '../types';

export const dataTypes = (
  intl: IntlShape
): Record<string, { icon: TablerIcon; label: string }> => {
  return {
    string: {
      icon: IconAbc,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.string',
      }),
    },
    number: {
      icon: IconNumber123,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.number',
      }),
    },
    date: {
      icon: IconCalendar,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.date',
      }),
    },
    object: {
      icon: IconBraces,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.object',
      }),
    },
    array: {
      icon: IconBrackets,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.array',
      }),
    },
    bool: {
      icon: IconCheckbox,
      label: intl.formatMessage({
        id: 'features.datasets.settings.form.columnsSection.type.bool',
      }),
    },
  };
};

export const useColumnHeaderData = ({
  control,
  attributeName,
  type,
  intl,
}: {
  control: Control<FormSchema>;
  attributeName: string;
  type: string;
  intl: IntlShape;
}): { icon: TablerIcon; title: ReactNode; description: ReactNode } => {
  const name = useWatch({
    control,
    name: `metadata.columnNames.${attributeName}`,
  });

  const dataType = dataTypes(intl)[type];
  const icon = dataType?.icon ?? IconQuestionMark;

  if (name) {
    return {
      icon,
      title: name,
      description: attributeName,
    };
  }

  return {
    icon,
    title: (
      <Typography variant="h6" color="text.secondary">
        {intl.formatMessage({
          id: 'features.datasets.settings.form.columnsSection.newColumn',
        })}
      </Typography>
    ),
    description: intl.formatMessage({
      id: 'features.datasets.settings.form.columnsSection.newColumnDescription',
    }),
  };
};
